import { type Dtos } from "@inrev/common";
import { useState } from "react";
import { DatePickerInput } from "../../../../form/inputs/DatePickerInput";
import { Input } from "../../../../form/inputs/Input";
import { YesNoInput } from "../../../../form/inputs/YesNoInput";
import { ClickableText, Text } from "../../../../primitives";
import { PanelField, type PanelFieldControlProps } from "../PanelField";
import {
	formatObservedValue,
	formatObservedValueDraft,
	type ObservedValueDraft,
	unformatObservedValueDraft,
} from "./observedValue";

export type ObservedValueFieldProps = {
	valueType: Dtos.Decisioning.FactorValueType;
	/** The value account data supplies when the underwriter does not enter one. */
	preview: ObservedValuePreview;
	overriding: boolean;
	onOverridingChange: (overriding: boolean) => void;
	draft: ObservedValueDraft;
	onDraftChange: (draft: ObservedValueDraft) => void;
	invalid: boolean;
};

const ValueInput = ({
	id,
	labelId,
	valueType,
	draft,
	onDraftChange,
	invalid,
}: Pick<ObservedValueFieldProps, "valueType" | "draft" | "onDraftChange" | "invalid"> &
	PanelFieldControlProps) => {
	if (valueType === "boolean") {
		return (
			<div role="group" aria-labelledby={labelId}>
				<YesNoInput
					compact
					value={typeof draft === "boolean" ? draft : null}
					onChange={onDraftChange}
					testId="factor-sheet-value"
				/>
			</div>
		);
	}
	return (
		<TypedValueInput
			id={id}
			labelId={labelId}
			valueType={valueType}
			draft={draft}
			onDraftChange={onDraftChange}
			invalid={invalid}
		/>
	);
};

/** Shows invalid only once the field has been left, and groups a typed number on blur. */
const TypedValueInput = ({
	id,
	labelId,
	valueType,
	draft,
	onDraftChange,
	invalid,
}: Pick<ObservedValueFieldProps, "valueType" | "draft" | "onDraftChange" | "invalid"> &
	PanelFieldControlProps) => {
	const [touched, setTouched] = useState(false);
	const text = typeof draft === "string" ? draft : "";
	if (valueType === "date") {
		return (
			<div role="group" aria-labelledby={labelId}>
				<DatePickerInput compact value={text} onChange={onDraftChange} />
			</div>
		);
	}
	return (
		<Input
			id={id}
			compact
			value={text}
			error={invalid && touched}
			inputMode={valueType === "text" ? "text" : "decimal"}
			prefix={valueType === "currency" ? "$" : undefined}
			suffix={valueType === "percent" ? "%" : valueType === "ratio" ? "x" : undefined}
			testId="factor-sheet-value"
			onChange={(event) => onDraftChange(event.target.value)}
			onFocus={() => onDraftChange(unformatObservedValueDraft(text, valueType))}
			onBlur={() => {
				setTouched(true);
				onDraftChange(formatObservedValueDraft(text, valueType));
			}}
		/>
	);
};

/** The account-data value to show: `loading` while it is read, `none` when account data has none. */
export type ObservedValuePreview =
	| { status: "loading" }
	| { status: "none" }
	| { status: "value"; value: Dtos.Decisioning.FactorValue }
	/** The read failed; the server still reads it on save. */
	| { status: "unknown" };

const previewSummary = (
	preview: ObservedValuePreview,
	valueType: Dtos.Decisioning.FactorValueType,
): string => {
	switch (preview.status) {
		case "value":
			return formatObservedValue(preview.value, valueType);
		case "loading":
			return "Reading account data…";
		case "none":
			return "No account data for this factor";
		case "unknown":
			return "Read from account data when saved";
	}
};

/** The value a pro/con is about: read from account data unless the underwriter enters their own. */
export const ObservedValueField = ({
	valueType,
	preview,
	overriding,
	onOverridingChange,
	draft,
	onDraftChange,
	invalid,
}: ObservedValueFieldProps) => {
	if (overriding) {
		return (
			<PanelField
				label="Value"
				helper={
					<ClickableText
						type="caption"
						color="link"
						testId="factor-sheet-value-use-data"
						onClick={() => onOverridingChange(false)}
					>
						Use account data instead
					</ClickableText>
				}
			>
				{({ id, labelId }) => (
					<ValueInput
						id={id}
						labelId={labelId}
						valueType={valueType}
						draft={draft}
						onDraftChange={onDraftChange}
						invalid={invalid}
					/>
				)}
			</PanelField>
		);
	}

	const hasValue = preview.status === "value";

	return (
		<PanelField
			label="Value"
			helper={preview.status === "none" ? "Enter a value if you have one." : undefined}
		>
			<div className="flex items-baseline justify-between gap-3">
				<Text
					type={hasValue ? "body" : "caption"}
					color={hasValue ? "primary" : "tertiary"}
					className="min-w-0 font-normal"
					aria-live="polite"
					data-testid="factor-sheet-value-summary"
				>
					{previewSummary(preview, valueType)}
				</Text>
				<ClickableText
					type="caption"
					color="link"
					testId="factor-sheet-value-override"
					onClick={() => onOverridingChange(true)}
				>
					{hasValue ? "Override" : "Enter value"}
				</ClickableText>
			</div>
		</PanelField>
	);
};
